<template>
  <div class="rental-deductions-container">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".rental-deductions-container"
                @cancel="closeHelp">
      <occupancy-rental-deductions-help :openSection="openHelpSection"></occupancy-rental-deductions-help>
    </res-dialog>
    <div class="grid-controls">
      <div class="help-controls">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="openHelp(null)" class="pb-2" color="var(--v-resBlue-darken1)">mdi-help-circle</v-icon>
          </template>
          <span>Help</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" class="ml-3 mb-2" :color="commentIconColor" @click="openComments">{{ commentIcon  }}</v-icon>
          </template>
          <span>Comments/Flags</span>
        </v-tooltip>
        <v-menu left offset-y close-on-click @input="updateMenuState">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon :color="menuColor" class="ellipses-control">
              <v-icon dense>{{menuIcon}}</v-icon>
            </v-btn>
          </template>
          <v-list class="ellipses-control-option-list">
            <v-list-item @click="openComments">
              <v-list-item-title class="ellipses-control-option">Add Flag or Comment</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-list-item v-bind="attrs" v-on="on" @click="openClearResetDialog">
                  <v-list-item-title class="ellipses-control-option">Clear & Reset</v-list-item-title>
                </v-list-item>
              </template>
              <span>This will clear this tab of any entered information and revert to the initial values</span>
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
    </div>
    <ag-grid-vue 
      style="width: 100%;"
      class="ag-theme-material"
      :gridOptions="gridOptions"
      :columnDefs="currentHeaders"
      :rowData="currentData"
      @cell-value-changed="onCellValueChanged">
    </ag-grid-vue>
    <clear-all-dialog 
      v-if="showClearResetDialog"
      :showDialog="showClearResetDialog"
      :maxWidth="480"
      :setWidth="480"
      @confirm="confirmClearResetDialog"
      @cancel="closeClearResetDialog">
    </clear-all-dialog>
  </div>
</template>

<script>
  // Row Types 
  const FORECASTED_MOVE_INS = 1;
  const FORECASTED_RENEWALS = 2;
  const RECURRING_MOVE_IN_CONCESSIONS = 3;
  const NEW_MOVE_IN_CONCESSIONS = 4;
  const TOTAL_MOVE_IN_CONCESSIONS = 5;
  const RECURRING_RENEWAL_CONCESSIONS = 6;
  const NEW_RENEWAL_CONCESSIONS = 7;
  const TOTAL_RENEWAL_CONCESSIONS = 8;
  const ADDITIONAL_CONCESSIONS = 9;
  const TOTAL_ADDITIONAL_CONCESSIONS = 10;
  const TOTAL_CONCESSIONS = 11;
  const ESTIMATED_SKIPS_AND_EVICTIONS = 12;
  const BAD_DEBT = 13;
  const TOTAL_BAD_DEBT = 14;

  import YearGrid from '@/mixins/YearGrid';
  import HelpableMixin from '@/mixins/Helpable.js';

  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog";
  import ResDialog from '@/components/base/ResDialog.vue';
  import { AgGridVue } from "ag-grid-vue";

  import OccupancyRentalDeductionsHelp from '@/components/help/OccupancyRentalDeductionsHelp.vue';

  import budgetService from '@/services/budget';
  import worksheetService from '@/services/worksheet';
  import occupancyService from '@/services/occupancy';
  import budgetGLAccountService from '@/services/budget-gl-account';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'ModelAdminDown',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      ClearAllDialog,
      AgGridVue,
      ResDialog,
      OccupancyRentalDeductionsHelp
    },
    props: {
      budgetIterationId: { 
        required: true
      },
      commentIteration: {},
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        budgetIterationLastModifiedTimestamp: null,
        isCalculating: false,
        summaryRefreshIsQueued: false,
        fiscalYear: null,
        fiscalYearStartMonth: null,
        fiscalYearHeaders: [],
        badDebtOption: null,
        availableColumns: [],
        newLeaseCalculationOption: 0,
        renewalPricingCalculationOption: 0,
        otherConcessionsTransactionCategoryIds: [], // facilitates propagating changes
        gridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          alignedGrids: [],
          singleClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          getRowClass: this.getRowClass
        },
        rentalDeductionsData: [],
        showClearResetDialog: false,
        tabHasUnresolvedComments: false,
        tabHasComments: false,
        menuOpen: false
      };
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      updateMenuState(isOpen) {
        this.menuOpen = isOpen;
      },
      // DOM load and update logic
      async load() {
        let t = this;

        t.showGridLoadingOverlay(t.gridOptions);

        await t.loadGridConfiguration();
        await t.loadRentalDeductionsData();

        t.sizeGridColumns(t.gridOptions);
        t.hideGridLoadingOverlay(t.gridOptions);
      },
      async loadGridConfiguration() {
        let t = this;

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.badDebtOption = settings.badDebtOption;
        t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;


        // setup column headers
        t.fiscalYearHeaders = t.getFiscalYearMonths(t.fiscalYearStartMonth);
        t.availableColumns = [
          { headerName: "", field: "groupTag", width: 16, pinned: 'left', suppressSizeToFit: true, cellClass: 'readonly-column' },
          { 
            headerName: "", field: "primaryLabel", width: 240, pinned: 'left', suppressSizeToFit: true,
            cellClass: 'readonly-column',
            cellStyle: function(params) {
              var style = { 'margin-left': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'right' };
              if (params.data.primarySubLabel) {
                style['line-height'] = 'normal'; 
              }
              return style;
            },
            cellRenderer: function(params) {
              let primaryLabelStyle = 'bold';
              return params.data.primarySubLabel ? 
              `<span style="font-weight: ${primaryLabelStyle}">${params.data.primaryLabel}</span>
               <br/>
               <span style="font-weight: normal">${params.data.primarySubLabel}</span>` : 
              `<span>${params.data.primaryLabel}</span>`;
            }
          },
          { 
            headerName: "", field: "year", width: 60, pinned: 'left', suppressSizeToFit: true,
            cellClass: 'readonly-column',
            cellStyle: { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' }
          },
          { headerName: t.fiscalYearHeaders[0], field: "periodIndex00Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[1], field: "periodIndex01Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[2], field: "periodIndex02Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[3], field: "periodIndex03Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[4], field: "periodIndex04Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[5], field: "periodIndex05Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[6], field: "periodIndex06Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[7], field: "periodIndex07Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[8], field: "periodIndex08Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[9], field: "periodIndex09Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[10], field: "periodIndex10Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: t.fiscalYearHeaders[11], field: "periodIndex11Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter },
          { headerName: "TOTAL/AVG", field: "totalOrAverage", width: 110, pinned: 'right', valueFormatter: t.cellFormatter, cellClass: 'readonly-column' }
        ];
      },
      async loadRentalDeductionsData() {
        let t = this;

        let response = await occupancyService.getRentalDeductionsEntries(t.budgetIterationId);
        t.newLeaseCalculationOption = response.newLeaseCalculationOption;
        t.renewalPricingCalculationOption = response.renewalPricingCalculationOption;

        t.rentalDeductionsData = [];
        t.otherConcessionsTransactionCategoryIds = [];

        // Forecasted # Move-Ins
        t.rentalDeductionsData.push(t.initRow(FORECASTED_MOVE_INS, response.forecastedMoveIns));
        // Forecasted # Renewals
        t.rentalDeductionsData.push(t.initRow(FORECASTED_RENEWALS, response.forecastedRenewals));
        // Recurring Move-In Concessions
        if (t.newLeaseCalculationOption == 3) {
          t.rentalDeductionsData.push(t.initRow(RECURRING_MOVE_IN_CONCESSIONS, response.recurringMoveInConcessions));
        }
        // New Move-In Concessions
        t.rentalDeductionsData.push(t.initRow(NEW_MOVE_IN_CONCESSIONS, response.newMoveInConcessions));
        // Total Move-In Concessions
        t.rentalDeductionsData.push(t.initRow(TOTAL_MOVE_IN_CONCESSIONS, response.totalMoveInConcessions));
        // Recurring Renewal Concessions
        if (t.renewalPricingCalculationOption == 3) {
          t.rentalDeductionsData.push(t.initRow(RECURRING_RENEWAL_CONCESSIONS, response.recurringRenewalConcessions));
        }
        // New Renewal Concessions
        t.rentalDeductionsData.push(t.initRow(NEW_RENEWAL_CONCESSIONS, response.newRenewalConcessions));
        // Total Renewal Concessions
        t.rentalDeductionsData.push(t.initRow(TOTAL_RENEWAL_CONCESSIONS, response.totalRenewalConcessions));
        // Additional Concessions
        let sequence = 1;
        (response.additionalConcessions).forEach((ac) => {
          let additionalConcession = t.initRow(ADDITIONAL_CONCESSIONS, ac);
          additionalConcession.sequence = sequence;
          sequence++;
          t.rentalDeductionsData.push(additionalConcession);
          t.otherConcessionsTransactionCategoryIds.push(ac.transactionCategoryId);
        });
        // Total Additional Concessions
        t.rentalDeductionsData.push(t.initRow(TOTAL_ADDITIONAL_CONCESSIONS, response.totalAdditionalConcessions));
        // Total Concessions
        t.rentalDeductionsData.push(t.initRow(TOTAL_CONCESSIONS, response.totalConcessions));
        // Estimated Skips/Evictions
        t.rentalDeductionsData.push(t.initRow(ESTIMATED_SKIPS_AND_EVICTIONS, response.estimatedSkipsAndEvictions));
        // Bad Debt
        t.rentalDeductionsData.push(t.initRow(BAD_DEBT, response.badDebt));
        // Total Bad Debt
        t.rentalDeductionsData.push(t.initRow(TOTAL_BAD_DEBT, response.totalBadDebt));
      },
      // row initialization logic
      initRow(rowType, data) {
        return {
          // identifiers
          rowType: rowType,
          transactionCategoryId: data.transactionCategoryId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          contentChangedSinceLastRefresh: false, // only used by gl mapping for Other Concessions

          // displayed columns
          primaryLabel: data.label,
          primarySubLabel: data.subLabel,
          year: data.year,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          totalOrAverage: data.total,
          
          // grouping metadata
          isGroupParent: false,
          groupTag: null,
          isGroupCollapsed: false
        }
      },
      getRowKey(data) {
        return this.generateRowKey(data.rowType, data.transactionCategoryId, data.year);
      },
      generateRowKey(rowType, transactionCategoryId, year) {
        return transactionCategoryId ? `${rowType}-${transactionCategoryId.toLowerCase()}-${year}` : `${rowType}-${year}`;
      },
      getRowClass(params) {
        var classlist = [];
        switch(params.data.rowType) {
          case FORECASTED_RENEWALS:
            return 'readonly-row';
          case RECURRING_MOVE_IN_CONCESSIONS:
          case RECURRING_RENEWAL_CONCESSIONS:  
            return 'top-lined-row';
          case FORECASTED_MOVE_INS:
          case TOTAL_MOVE_IN_CONCESSIONS:
          case TOTAL_RENEWAL_CONCESSIONS:
          case TOTAL_ADDITIONAL_CONCESSIONS:
          case TOTAL_CONCESSIONS:
          case ESTIMATED_SKIPS_AND_EVICTIONS:
          case TOTAL_BAD_DEBT:
            return 'readonly-row top-lined-row';
          case BAD_DEBT:
            return this.badDebtOption === 3 && !this.readonly ? '' : 'readonly-row';
          case NEW_MOVE_IN_CONCESSIONS:
            if (this.readonly) {
              classlist.push('readonly-row');
            }
            if (this.newLeaseCalculationOption < 3) {
              classlist.push('top-lined-row');
            }
            return classlist.join(' ');
          case NEW_RENEWAL_CONCESSIONS:
            if (this.readonly) {
              classlist.push('readonly-row');
            }
            if (this.newLeaseCalculationOption < 3) {
              classlist.push('top-lined-row');
            }
            return classlist.join(' ');
          case ADDITIONAL_CONCESSIONS:
            if (this.readonly) {
              classlist.push('readonly-row');
            }
            if (this.newLeaseCalculationOption < 3) {
              classlist.push('top-lined-row');
            }
            return classlist.join(' ');
          default:
            return '';
        }
      },
      isRowEditable(rowType) {
        switch (rowType) {
          case RECURRING_MOVE_IN_CONCESSIONS:
          case NEW_MOVE_IN_CONCESSIONS:
          case RECURRING_RENEWAL_CONCESSIONS:
          case NEW_RENEWAL_CONCESSIONS:
          case ADDITIONAL_CONCESSIONS:
            return true;
          case BAD_DEBT:
            return this.badDebtOption === 3;
          default:
            return false;
        }
      },
      isCellEditable(params) {
        if (this.readonly) {
          return false;
        }
        if (this.readonly || this.isCalculating) {
          return false; // view is readonly or is currently calculating
        }
        if (!this.isRowEditable(params.data.rowType)) {
          return false; // row type is not editable
        }
        return this.isPeriodIndexAmountColumn(params.colDef.field);
      },
      // row update logic
      async onCellValueChanged(params) {
        let t = this;

        // do not need to recalculate if a row changed that users cannot edit
        if (!t.isRowEditable(params.data.rowType)) {
          return;
        }
        // do not need to recalculate if the cell value did not actually change
        if (params.oldValue == params.newValue) {
          return;
        }
        // do not need to recalculate if a cell value changed that was not a period index amount
        if (!t.isPeriodIndexAmountColumn(params.colDef.field)) {
          return;
        }

        t.isCalculating = true;

        // prepare for calculations
        let editedRowType = params.data.rowType;
        let editedPeriodIndex = t.getPeriodIndexFromColumn(params.colDef.field);
        let rowNodeDataToUpdate = []; // track updated rows

        let recurringMoveInConcessionsRowData = editedRowType == RECURRING_MOVE_IN_CONCESSIONS ? params.data :
          (t.newLeaseCalculationOption == 3 ? t.gridOptions.api.getRowNode(this.generateRowKey(RECURRING_MOVE_IN_CONCESSIONS, null, t.fiscalYear)).data : null);

        let newMoveInConcessionsRowData = editedRowType == NEW_MOVE_IN_CONCESSIONS ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(NEW_MOVE_IN_CONCESSIONS, null, t.fiscalYear)).data;

        let totalMoveInConcessionsRowData = editedRowType == TOTAL_MOVE_IN_CONCESSIONS ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(TOTAL_MOVE_IN_CONCESSIONS, null, t.fiscalYear)).data;

        let recurringRenewalConcessionsRowData = editedRowType == RECURRING_RENEWAL_CONCESSIONS ? params.data :
          (t.renewalPricingCalculationOption == 3 ? t.gridOptions.api.getRowNode(this.generateRowKey(RECURRING_RENEWAL_CONCESSIONS, null, t.fiscalYear)).data : null);
       
        let newRenewalConcessionsRowData = editedRowType == NEW_RENEWAL_CONCESSIONS ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(NEW_RENEWAL_CONCESSIONS, null, t.fiscalYear)).data;

        let totalRenewalConcessionsRowData = editedRowType == TOTAL_RENEWAL_CONCESSIONS ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(TOTAL_RENEWAL_CONCESSIONS, null, t.fiscalYear)).data;
        
        let additionalConcessionsRowData = [];
        t.otherConcessionsTransactionCategoryIds.forEach((id) => {
          let rowData = editedRowType == ADDITIONAL_CONCESSIONS && params.data.transactionCategoryId == id ? params.data :
            t.gridOptions.api.getRowNode(this.generateRowKey(ADDITIONAL_CONCESSIONS, id, t.fiscalYear)).data;
          additionalConcessionsRowData.push(rowData);
        });

        let totalAdditionalConcessionsRowData = editedRowType == TOTAL_ADDITIONAL_CONCESSIONS ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(TOTAL_ADDITIONAL_CONCESSIONS, null, t.fiscalYear)).data;
        
        let totalConcessionsRowData = editedRowType == TOTAL_CONCESSIONS ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(TOTAL_CONCESSIONS, null, t.fiscalYear)).data;

        let badDebtRowData = editedRowType == BAD_DEBT ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(BAD_DEBT, null, t.fiscalYear)).data;
        
        let totalBadDebtRowData = editedRowType == TOTAL_BAD_DEBT ? params.data :
          t.gridOptions.api.getRowNode(this.generateRowKey(TOTAL_BAD_DEBT, null, t.fiscalYear)).data;
        
        let recalculateTotalMoveInConcessions = false;
        let recalculationTotalRenewalConcessions = false;
        let recalculateTotalAdditionalConcessions = false;
        let recalculateTotalConcessions = false;
        let recalculateTotalBadDebt = false;
        let editedRowData = null;
        switch (editedRowType) {
          case RECURRING_MOVE_IN_CONCESSIONS:
            editedRowData = recurringMoveInConcessionsRowData;
            recalculateTotalMoveInConcessions = true;
            recalculateTotalConcessions = true;
            break;
          case NEW_MOVE_IN_CONCESSIONS:
            editedRowData = newMoveInConcessionsRowData;
            recalculateTotalMoveInConcessions = true;
            recalculateTotalConcessions = true;
            break;
          case RECURRING_RENEWAL_CONCESSIONS:
            editedRowData = recurringRenewalConcessionsRowData;
            recalculationTotalRenewalConcessions = true;
            recalculateTotalConcessions = true;
            break;
          case NEW_RENEWAL_CONCESSIONS:
            editedRowData = newRenewalConcessionsRowData;
            recalculationTotalRenewalConcessions = true;
            recalculateTotalConcessions = true;
            break;
          case ADDITIONAL_CONCESSIONS:
            for (let i = 0; i < additionalConcessionsRowData.length; i++) {
              let rowData = additionalConcessionsRowData[i];
              if (rowData.transactionCategoryId == params.data.transactionCategoryId) {
                editedRowData = rowData;
                editedRowData.contentChangedSinceLastRefresh = true;
                break;
              }
            }
            recalculateTotalAdditionalConcessions = true;
            recalculateTotalConcessions = true;
            break;
          case BAD_DEBT:
            editedRowData = badDebtRowData;
            recalculateTotalBadDebt = true;
            break;
          default:
            return;
        }

        // parse edited cell input and recalculate edited row's total
        let val = t.parseCurrency(t.getRowNodePeriodIndexAmount(editedRowData, editedPeriodIndex));
        t.setRowNodePeriodIndexAmount(editedRowData, editedPeriodIndex, val);
        editedRowData.totalOrAverage = t.calculateTotalAsCurrency(editedRowData);
        rowNodeDataToUpdate.push(editedRowData);

        // recalculate affected totals
        for (let periodIndex = 0; periodIndex < 12; periodIndex++) {
          // recalculate Move-In Concessions
          if (recalculateTotalMoveInConcessions) {
            let recurringMoveInConcessionsVal = recurringMoveInConcessionsRowData ? t.parseCurrency(t.getRowNodePeriodIndexAmount(recurringMoveInConcessionsRowData, periodIndex)) : 0;
            let newMoveInConcessionsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(newMoveInConcessionsRowData, periodIndex));
            let totalMoveInConcessionsVal = t.parseCurrency(recurringMoveInConcessionsVal + newMoveInConcessionsVal);
            t.setRowNodePeriodIndexAmount(totalMoveInConcessionsRowData, periodIndex, totalMoveInConcessionsVal);
            if (periodIndex == 11) {
              totalMoveInConcessionsRowData.totalOrAverage = t.calculateTotalAsCurrency(totalMoveInConcessionsRowData);
              rowNodeDataToUpdate.push(totalMoveInConcessionsRowData);
            }
          }

          // recalculate Renewal Concessions
          if (recalculationTotalRenewalConcessions) {
            let recurringRenewalsConcessionsVal = recurringRenewalConcessionsRowData ? t.parseCurrency(t.getRowNodePeriodIndexAmount(recurringRenewalConcessionsRowData, periodIndex)) : 0;
            let newRenewalConcessionsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(newRenewalConcessionsRowData, periodIndex));
            let totalRenewalConcessionsVal = t.parseCurrency(recurringRenewalsConcessionsVal + newRenewalConcessionsVal);
            t.setRowNodePeriodIndexAmount(totalRenewalConcessionsRowData, periodIndex, totalRenewalConcessionsVal);
            if (periodIndex == 11) {
              totalRenewalConcessionsRowData.totalOrAverage = t.calculateTotalAsCurrency(totalRenewalConcessionsRowData);
              rowNodeDataToUpdate.push(totalRenewalConcessionsRowData);
            }
          }

          // recalculate Additional Concessions
          if (recalculateTotalAdditionalConcessions) {
            let totalAdditionalConcessionsVal = 0;
            additionalConcessionsRowData.forEach((rowData) => {
              let additionalConcessionsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(rowData, periodIndex));
              totalAdditionalConcessionsVal += additionalConcessionsVal;
            });
            t.setRowNodePeriodIndexAmount(totalAdditionalConcessionsRowData, periodIndex, totalAdditionalConcessionsVal);
            if (periodIndex == 11) {
              totalAdditionalConcessionsRowData.totalOrAverage = t.calculateTotalAsCurrency(totalAdditionalConcessionsRowData);
              rowNodeDataToUpdate.push(totalAdditionalConcessionsRowData);
            }
          }

          // recalculate Total Concessions
          if (recalculateTotalConcessions) {
            let totalMoveInConcessionsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(totalMoveInConcessionsRowData, periodIndex));
            let totalRenewalConcessionsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(totalRenewalConcessionsRowData, periodIndex));
            let totalAdditionalConcessionsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(totalAdditionalConcessionsRowData, periodIndex));
            let totalConcessionsVal = t.parseCurrency(totalMoveInConcessionsVal + totalRenewalConcessionsVal + totalAdditionalConcessionsVal);
            t.setRowNodePeriodIndexAmount(totalConcessionsRowData, periodIndex, totalConcessionsVal);
            if (periodIndex == 11) {
              totalConcessionsRowData.totalOrAverage = t.calculateTotalAsCurrency(totalConcessionsRowData);
              rowNodeDataToUpdate.push(totalConcessionsRowData);
            }
          }

          // recalculate Total Bad Debt
          if (recalculateTotalBadDebt) {
            let badDebtVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(badDebtRowData, periodIndex));
            let totalBadDebtVal = badDebtVal;
            t.setRowNodePeriodIndexAmount(totalBadDebtRowData, periodIndex, totalBadDebtVal);
            if (periodIndex == 11) {
              totalBadDebtRowData.totalOrAverage = t.calculateTotalAsCurrency(totalBadDebtRowData);
              rowNodeDataToUpdate.push(totalBadDebtRowData);
            }
          }
        }

        // save updates to the front-end
        if (rowNodeDataToUpdate.length > 0) {
          t.gridOptions.api.applyTransactionAsync({ update: rowNodeDataToUpdate });
        }

        // prepare updates for the backend
        let propertyEntriesToPost = [];
        rowNodeDataToUpdate.filter(e => e.worksheetEntryTypeId).forEach((e) => {
          propertyEntriesToPost.push({
            worksheetEntryTypeId: e.worksheetEntryTypeId,
            periodIndex00: e.periodIndex00Amount,
            periodIndex01: e.periodIndex01Amount,
            periodIndex02: e.periodIndex02Amount,
            periodIndex03: e.periodIndex03Amount,
            periodIndex04: e.periodIndex04Amount,
            periodIndex05: e.periodIndex05Amount,
            periodIndex06: e.periodIndex06Amount,
            periodIndex07: e.periodIndex07Amount,
            periodIndex08: e.periodIndex08Amount,
            periodIndex09: e.periodIndex09Amount,
            periodIndex10: e.periodIndex10Amount,
            periodIndex11: e.periodIndex11Amount,
          });
        });
        let additionalConcessionsToPost = [];
        rowNodeDataToUpdate.filter(e => e.transactionCategoryId).forEach((e) => {
          additionalConcessionsToPost.push({
            transactionCategoryId: e.transactionCategoryId,
            periodIndex00: e.periodIndex00Amount,
            periodIndex01: e.periodIndex01Amount,
            periodIndex02: e.periodIndex02Amount,
            periodIndex03: e.periodIndex03Amount,
            periodIndex04: e.periodIndex04Amount,
            periodIndex05: e.periodIndex05Amount,
            periodIndex06: e.periodIndex06Amount,
            periodIndex07: e.periodIndex07Amount,
            periodIndex08: e.periodIndex08Amount,
            periodIndex09: e.periodIndex09Amount,
            periodIndex10: e.periodIndex10Amount,
            periodIndex11: e.periodIndex11Amount,
          });
        });

        // send updates to the backend
        if (propertyEntriesToPost.length > 0) {
          await worksheetService.updatePropertyWorksheetEntries(t.budgetIterationId, propertyEntriesToPost, t.budgetIterationLastModifiedTimestamp);
        }

        if (additionalConcessionsToPost.length > 0) {
          await occupancyService.updateOtherConcessionsEntries(t.budgetIterationId, additionalConcessionsToPost, t.budgetIterationLastModifiedTimestamp);
        }

        t.isCalculating = false;

        // refresh dependencies
        if (!t.summaryRefreshIsQueued) {
          t.summaryRefreshIsQueued = true;
          setTimeout(async () => {
            t.summaryRefreshIsQueued = false;
            t.$eventHub.$emit('rentalDeductionEntriesUpdated', t.budgetIterationId);

            var worksheetEntryTypeIds = [25, 26, 27, 28, 29];
            /*  
                Enums.WorksheetEntryTypes.RecurringMoveInConcessionsAmount,
                Enums.WorksheetEntryTypes.NewMoveInConcessionsAmount,
                Enums.WorksheetEntryTypes.RecurringRenewalConcessionsAmount,
                Enums.WorksheetEntryTypes.NewRenewalConcessionsAmount,
                Enums.WorksheetEntryTypes.BadDebtAmount
            */
            // this should only update other concessions which have changed since the last time this was run
            let additionalConcessionsTransactionCategoryIds = [];
            let postMappingUpdateData = [];
            t.otherConcessionsTransactionCategoryIds.forEach((id) => {
              let thsData = t.gridOptions.api.getRowNode(this.generateRowKey(ADDITIONAL_CONCESSIONS, id, t.fiscalYear)).data;
              if (thsData.contentChangedSinceLastRefresh) {
                additionalConcessionsTransactionCategoryIds.push(id);
                thsData.contentChangedSinceLastRefresh = false;
                postMappingUpdateData.push(thsData);
              }              
            });           
            await budgetGLAccountService.updateBasedOnWorksheets(t.budgetIterationId, worksheetEntryTypeIds, additionalConcessionsTransactionCategoryIds, t.budgetIterationLastModifiedTimestamp);

            if (postMappingUpdateData.length > 0) {
              t.gridOptions.api.applyTransactionAsync({ update: postMappingUpdateData });
            }
          }, 2500); // reduce refresh calls when multiple edits happen at once
        }
      },
      cellFormatter(params) {
        switch (params.data.rowType) {
          // dollar amounts
          case RECURRING_MOVE_IN_CONCESSIONS:
          case NEW_MOVE_IN_CONCESSIONS:
          case TOTAL_MOVE_IN_CONCESSIONS:
          case RECURRING_RENEWAL_CONCESSIONS:
          case NEW_RENEWAL_CONCESSIONS:
          case TOTAL_RENEWAL_CONCESSIONS:
          case ADDITIONAL_CONCESSIONS:
          case TOTAL_ADDITIONAL_CONCESSIONS:
          case TOTAL_CONCESSIONS:
          case BAD_DEBT:
          case TOTAL_BAD_DEBT:
            return this.formatCurrency(params.value, true, true);
          // integers
          case FORECASTED_MOVE_INS:
          case FORECASTED_RENEWALS:
          case ESTIMATED_SKIPS_AND_EVICTIONS:
            return this.formatInteger(params.value);
          default:
            return params.value?.toString();
        }
      },
      // clear and reset logic
      openClearResetDialog() {
        if (this.readonly) {
          this.$emit('denied-status');
          return;
        }
        this.showClearResetDialog = true;
      },
      closeClearResetDialog() {
        this.showClearResetDialog = false;
      },
      async confirmClearResetDialog() {
        this.changeLoading(true);
        
        let response = await occupancyService.setDefaultRentalDeductionsEntries(this.budgetIterationId);
        if (response) {
          this.showGridLoadingOverlay(this.gridOptions);
          await this.loadRentalDeductionsData();
          this.hideGridLoadingOverlay(this.gridOptions);
          this.$eventHub.$emit('rentalDeductionEntriesUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Rental Deductions entries cleared and reset successfully!');
        }
        else {
          this.activateErrorDialog({title: 'Clear & Reset Failed', message: 'An error occurred clearing and resetting the data. Please try again.'});
        }
        this.closeClearResetDialog();

        this.changeLoading(false);
      },
      // comments logic
      openComments() {
        this.$emit('openComments', 'rentaldeductions');
      },
      async refreshCommentIcon() {
        var commentsResults = await worksheetService.commentsExist(this.budgetIterationId, 7);
        this.tabHasUnresolvedComments = commentsResults.unresolvedFlagExists;
        this.tabHasComments = commentsResults.hasComments;
        console.debug('refresh comment rental deductions');
      }
    },
    computed: {
    ...mapGetters(['currentLoading']),
      currentData() {
        return this.rentalDeductionsData;
      },
      currentHeaders() { 
        return this.availableColumns;
      },
      commentIconColor() {
        return this.tabHasUnresolvedComments ? 'var(--v-resRed-darken1)' : 'var(--v-resBlue-darken1)';
      },
      commentIcon() {
        return this.tabHasUnresolvedComments ? 'mdi-flag' : this.tabHasComments ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline';
      },
      menuIcon() {
        return this.menuOpen ? 'mdi-dots-horizontal-circle' : 'mdi-dots-horizontal';
      },
      menuColor() {
        return 'var(--v-resBlue-darken1)';
      }
    },
    watch: {
      commentIteration: function () {
        this.refreshCommentIcon();
      }
    },
    async mounted() {
      this.changeLoading(true);
      await this.load();
      await this.refreshCommentIcon();
      this.changeLoading(false);
    },
    async created() {
      let t = this;

      t.$eventHub.$on('modelAdminDownEntriesUpdated', async (budgetIterationId) => {
        if (budgetIterationId == t.budgetIterationId) {
          await t.loadRentalDeductionsData();
        }
      });
      t.$eventHub.$on('moveInMoveOutEntriesUpdated', async (budgetIterationId) => {
        if (budgetIterationId == t.budgetIterationId) {
          await t.loadRentalDeductionsData();
        }
      });
    }
  };
</script>

<style lang="scss" scoped>
.v-input--selection-controls::v-deep {
  margin: 0 !important;
  padding: 0 !important;
}
.grid-controls {
  height: 40px;
  margin-top: 14px;
  margin-bottom: 14px;
}
.help-controls {
  height: 100%;
  float: right;
  border: solid 1px var(--v-resGrey-lighten3);
  padding: 7px 6px 6px 6px;
  cursor: pointer;
}
.ellipses-control {
  height: 20px;
  width: 20px;
  margin-left: 10px;
  margin-bottom: 8px;
}
.ellipses-control-option-list {
  border-radius: 6px;
  border: 1px solid var(--v-resBlue-darken1);
}
.ellipses-control-option {
  font-size: 13px;
}
</style>
